import { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  restrictApi,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import { CommonQueryKeys } from '~/common/api';
import { useApi, useApplicationContext, useConfig } from '~/common/context';
import {
  CommonErrorCodesEnumType,
  ITenant,
  ITenantUpdatePayload,
} from '~/common/types';

type Options = {
  getCurrent?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<ITenant>, IErrorApiResponse>
  >;
  updateTenant?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse<undefined, CommonErrorCodesEnumType, string[]>,
    ITenantUpdatePayload
  >;
};
export const useManageTenants = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { auth } = useApplicationContext();
  const { tenantCode } = useConfig();

  const getCurrentTenantQuery = useQuery({
    ...options.getCurrent,
    queryKey: CommonQueryKeys.tenants.base(tenantCode),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ITenant>>('/tenants/api/tenants/current'),
  });

  const updateTenantMutation = useMutation({
    mutationFn: (settings) =>
      callCoreApi('/tenants/api/tenants', {
        method: 'PUT',
        body: JSON.stringify(settings),
      }),
    keysToInvalidateOnSuccess: [
      CommonQueryKeys.tenants.base(tenantCode),
      CommonQueryKeys.appContext(auth.userId as string),
      { key: CommonQueryKeys.parameters.groups(), type: 'all' },
      CommonQueryKeys.appConfig(),
    ],
    ...options?.updateTenant,
  });
  return restrictApi(options, {
    updateTenant: updateTenantMutation,
    getCurrent: getCurrentTenantQuery,
  });
};
